import { useEffect } from 'react';
import { useAtom } from 'jotai';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { execute } from '@/graphql/execute';
import { getProjectJobsQuery } from '@/queries/project.query';
import { layerByproductsQuery } from '@/queries/byproduct.query';
import { clipboxExportTrackingAtom } from '@/atoms/clipboxExport-progress.atom';

/**
 * Mounts once per project shell (alongside `useDeviationProgress` and
 * `useGroundClassifyProgress`). Same shape as those hooks, with one key
 * difference: a clipbox export does NOT produce a new viewable layer —
 * the result is a download-only byproduct attached to the source layer's
 * `sourceAttachmentId`. So success is detected by the parent job
 * transitioning to `PROCESSED`, not by a new derived layer appearing.
 *
 * On success we resolve the loading toast into a success toast that
 * carries a Download action. The action lazily fetches `layerByproducts`
 * for the source layer, picks the row whose `processJobId` matches the
 * just-completed job, and opens its presigned URL.
 *
 * Polling cadence: explicit `setInterval(..., 2000)` driven by
 * `hasTrackers`, calling `refetch()` directly. Same rationale as
 * `useDeviationProgress`: shared query keys with page-level subscribers
 * make `refetchInterval` flaky.
 */
export function useClipboxExportProgress(projectId: string) {
  const { t } = useTranslation('project');
  const [trackers, setTrackers] = useAtom(clipboxExportTrackingAtom);

  const projectTrackers = trackers.filter((tr) => tr.projectId === projectId);
  const hasTrackers = projectTrackers.length > 0;

  const { data: jobsData, refetch: refetchJobs } = useQuery({
    queryKey: ['projectJobs', projectId],
    queryFn: () => execute(getProjectJobsQuery, { projectId }),
    enabled: !!projectId && hasTrackers,
  });

  useEffect(() => {
    if (!hasTrackers || !projectId) return;
    const tick = () => {
      void refetchJobs();
    };
    tick();
    const id = window.setInterval(tick, 2000);
    return () => window.clearInterval(id);
  }, [hasTrackers, projectId, refetchJobs]);

  useEffect(() => {
    if (!hasTrackers) return;
    const jobs = jobsData?.jobs ?? [];

    setTrackers((prev) => {
      const next = [...prev];
      let changed = false;

      for (let i = next.length - 1; i >= 0; i--) {
        const tracker = next[i];
        if (tracker.projectId !== projectId) continue;

        // The CLIPBOX_EXPORT job is the only leaf in its synthetic batch,
        // so matching by `batchId` (and type, defensively) is unambiguous.
        const job = jobs.find(
          (j) => j.batchId === tracker.batchId && j.type === 'CLIPBOX_EXPORT',
        );
        if (!job) continue;

        if (job.status === 'FAILED') {
          // worker.py + index.ts surface a precise error message via
          // job.completeFailure (e.g. "Clipbox is empty …"). Prefer that
          // over the generic localized failure copy when present so the
          // user sees the actionable detail.
          const detail =
            (typeof job.errorLogs === 'string' && job.errorLogs.trim().length > 0
              ? job.errorLogs.trim()
              : null) ?? null;
          toast.error(
            detail ?? t('clipboxExport.failedToast', { name: tracker.sourceLayerName }),
            { id: tracker.toastId, duration: 6000 },
          );
          next.splice(i, 1);
          changed = true;
          continue;
        }

        if (job.status === 'PROCESSED') {
          const completedJobId = job.id;
          // Resolve the loading toast in place. The Download action does
          // an on-demand byproduct lookup so we don't have to keep an
          // extra query alive after the run finishes.
          toast.success(
            t('clipboxExport.completedToast', {
              name: tracker.sourceLayerName,
              format: tracker.format,
            }),
            {
              id: tracker.toastId,
              duration: 10000,
              action: {
                label: t('clipboxExport.download'),
                onClick: async () => {
                  try {
                    const data = await execute(layerByproductsQuery, {
                      layerId: tracker.sourceLayerId,
                    });
                    const bp = data.layerByproducts.find(
                      (b) => b.processJobId === completedJobId,
                    );
                    if (bp) {
                      window.open(bp.downloadUrl, '_blank');
                    } else {
                      toast.error(t('clipboxExport.byproductNotFound'));
                    }
                  } catch (err) {
                    toast.error(err instanceof Error ? err.message : String(err));
                  }
                },
              },
            },
          );
          next.splice(i, 1);
          changed = true;
        }
      }

      return changed ? next : prev;
    });
  }, [jobsData, hasTrackers, projectId, setTrackers, t]);
}
